import React, { useEffect, useState, useReducer, useContext } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  makeStyles,
  Paper,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Box
} from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { format, parseISO, isSameDay } from "date-fns";
import { green } from "@material-ui/core/colors";
import clsx from "clsx";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import MarkdownWrapper from "../../components/MarkdownWrapper";
import ModalImageCors from "../../components/ModalImageCors";
import LocationPreview from "../../components/LocationPreview";
import VcardPreview from "../../components/VcardPreview";
import AudioModal from "../../components/AudioModal";

import {
  Done,
  DoneAll,
  AccessTime,
} from "@material-ui/icons";

// CSS global para remover AppBar
const globalStyles = `
  body.ticket-embed-mode .MuiAppBar-root,
  body.ticket-embed-mode .MuiDrawer-root,
  body.ticket-embed-mode nav,
  body.ticket-embed-mode header {
    display: none !important;
  }
  body.ticket-embed-mode {
    overflow: hidden !important;
  }
`;

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    width: "100vw",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "fixed",
    top: 0,
    left: 0,
    "& .MuiAppBar-root": {
      display: "none !important",
    },
    "& .MuiDrawer-root": {
      display: "none !important",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  contactInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  messagesWrapper: {
    backgroundImage: `url(${whatsBackground})`,
    backgroundColor: "#e5ddd5",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  messageContainer: {
    display: "flex",
    marginBottom: theme.spacing(1),
  },
  messageLeft: {
    justifyContent: "flex-start",
  },
  messageRight: {
    justifyContent: "flex-end",
  },
  messageBubble: {
    maxWidth: "65%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    wordWrap: "break-word",
    position: "relative",
  },
  messageBubbleLeft: {
    backgroundColor: "#ffffff",
    borderTopLeftRadius: 0,
    color: "#000",
  },
  messageBubbleRight: {
    backgroundColor: "#dcf8c6",
    borderTopRightRadius: 0,
    color: "#000",
  },
  messageText: {
    fontSize: "14px",
    lineHeight: "19px",
    marginBottom: 4,
    color: "#000",
  },
  contactName: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#00897b",
    marginBottom: 4,
  },
  userName: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#0088cc",
    marginBottom: 4,
  },
  messageTime: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    fontSize: "11px",
    color: "rgba(0, 0, 0, 0.45)",
    marginTop: 4,
    gap: 4,
  },
  ackIcons: {
    fontSize: 16,
    color: "rgba(0, 0, 0, 0.45)",
  },
  ackDoneAll: {
    color: green[500],
  },
  divider: {
    display: "flex",
    justifyContent: "center",
    margin: theme.spacing(2, 0),
  },
  dividerText: {
    backgroundColor: "#e1f3fb",
    padding: theme.spacing(0.5, 2),
    borderRadius: 8,
    fontSize: "12px",
    color: "#667781",
  },
  downloadMedia: {
    padding: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: 8,
    "& a": {
      textDecoration: "none",
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    padding: theme.spacing(3),
    textAlign: "center",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...state, ...newMessages];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const TicketEmbed = () => {
  const classes = useStyles();
  const { ticketId } = useParams();
  const location = useLocation();
  
  // Não usar AuthContext para evitar erros quando está fora do provider
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const socket = authContext?.socket;
  
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pegar token da query string se estiver em modo embed
  const queryParams = new URLSearchParams(location.search);
  const embedToken = queryParams.get('embedToken') || queryParams.get('token');

  // Adicionar classe ao body e injetar CSS global
  useEffect(() => {
    // Adicionar classe ao body
    document.body.classList.add('ticket-embed-mode');
    
    // Injetar CSS global
    const styleElement = document.createElement('style');
    styleElement.id = 'ticket-embed-styles';
    styleElement.textContent = globalStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      // Remover classe e styles ao desmontar
      document.body.classList.remove('ticket-embed-mode');
      const styles = document.getElementById('ticket-embed-styles');
      if (styles) {
        styles.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (!ticketId) {
      setError("ID do ticket não fornecido");
      setLoading(false);
      return;
    }

    const fetchTicket = async () => {
      try {
        setLoading(true);
        setError(null);

        // Configurar headers
        const headers = embedToken ? { 'X-Embed-Token': embedToken } : {};

        // Se tiver token, usar rotas embed específicas
        const ticketUrl = embedToken ? `/embed/tickets/${ticketId}` : `/tickets/${ticketId}`;
        const messagesUrl = embedToken ? `/embed/messages/${ticketId}` : `/messages/${ticketId}`;

        const { data } = await api.get(ticketUrl, { headers });
        setTicket(data);

        const { data: messagesData } = await api.get(messagesUrl, { headers });
        dispatch({ type: "LOAD_MESSAGES", payload: messagesData.messages });
      } catch (err) {
        console.error("Erro ao carregar ticket:", err);
        setError(err.response?.data?.error || "Erro ao carregar as mensagens do ticket");
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [ticketId, embedToken]);

  useEffect(() => {
    if (!socket || !ticketId || !user) return;

    const onAppMessage = (data) => {
      if (data.action === "create" && data.message.ticketId === parseInt(ticketId)) {
        dispatch({ type: "LOAD_MESSAGES", payload: [data.message] });
      }
    };

    socket.on(`company-${user.companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${user.companyId}-appMessage`, onAppMessage);
    };
  }, [socket, ticketId, user]);

  const renderMessageDivider = (message, index) => {
    if (index === 0) {
      return (
        <div className={classes.divider}>
          <span className={classes.dividerText}>
            {format(parseISO(message.createdAt), "dd/MM/yyyy")}
          </span>
        </div>
      );
    }
    if (index < messagesList.length) {
      const previousMessage = messagesList[index - 1];
      if (!isSameDay(parseISO(previousMessage.createdAt), parseISO(message.createdAt))) {
        return (
          <div className={classes.divider}>
            <span className={classes.dividerText}>
              {format(parseISO(message.createdAt), "dd/MM/yyyy")}
            </span>
          </div>
        );
      }
    }
    return null;
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll className={clsx(classes.ackIcons, classes.ackDoneAll)} />;
    }
    return null;
  };

  const renderQuotedMessage = (message) => {
    if (!message.quotedMsg) return null;

    const cleanTemplateBody = (body) => {
      if (!body) return body;
      if (body.includes('||||')) {
        return body.split('||||')[0];
      }
      return body;
    };

    return (
      <div
        style={{
          borderLeft: "3px solid #0a0",
          paddingLeft: 8,
          marginBottom: 8,
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          borderRadius: 4,
          padding: 4,
        }}
      >
        <Typography variant="caption" style={{ fontWeight: "bold", display: "block" }}>
          {message.quotedMsg.contact?.name || "Contato"}
        </Typography>
        <Typography variant="caption" style={{ fontSize: "12px" }}>
          {cleanTemplateBody(message.quotedMsg.body)}
        </Typography>
      </div>
    );
  };

  const renderMessageContent = (message) => {
    if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    }

    if (message.mediaType === "audio") {
      return <AudioModal url={message.mediaUrl} />;
    }

    if (message.mediaType === "video") {
      return (
        <video
          controls
          style={{ maxWidth: "100%", borderRadius: 8 }}
          src={message.mediaUrl}
        />
      );
    }

    if (message.mediaType === "application" || message.mediaType === "document") {
      return (
        <div className={classes.downloadMedia}>
          <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer">
            📄 {message.body || "Download"}
          </a>
        </div>
      );
    }

    if (message.mediaType === "vcard") {
      return <VcardPreview contact={message.body} />;
    }

    if (message.mediaType === "location" && message.body) {
      const locationData = message.body.split("|");
      return (
        <LocationPreview
          latitude={locationData[0]}
          longitude={locationData[1]}
          name={locationData[2]}
          address={locationData[3]}
        />
      );
    }

    return (
      <MarkdownWrapper>
        {message.body}
      </MarkdownWrapper>
    );
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.root}>
        <div className={classes.errorContainer}>
          <Typography variant="h6" color="error" gutterBottom>
            Erro ao carregar ticket
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {error}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.messagesWrapper}>
        {messagesList.length === 0 ? (
          <Box textAlign="center" mt={4}>
            <Typography variant="body2" color="textSecondary">
              Nenhuma mensagem encontrada
            </Typography>
          </Box>
        ) : (
          messagesList.map((message, index) => (
            <React.Fragment key={message.id}>
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageContainer, {
                  [classes.messageLeft]: !message.fromMe,
                  [classes.messageRight]: message.fromMe,
                })}
              >
                <Paper
                  className={clsx(classes.messageBubble, {
                    [classes.messageBubbleLeft]: !message.fromMe,
                    [classes.messageBubbleRight]: message.fromMe,
                  })}
                  elevation={1}
                >
                  {!message.fromMe && (
                    <div className={classes.contactName}>
                      {message.contact?.name || ticket?.contact?.name || "Contato"}
                    </div>
                  )}
                  {message.fromMe && message.user && (
                    <div className={classes.userName}>
                      {message.user.name}
                    </div>
                  )}
                  {renderQuotedMessage(message)}
                  <div className={classes.messageText}>
                    {renderMessageContent(message)}
                  </div>
                  <div className={classes.messageTime}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {message.fromMe && renderMessageAck(message)}
                  </div>
                </Paper>
              </div>
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

export default TicketEmbed;
